/**
 * Provider costs arrive as decimal dollar strings ("0.0040"). They are stored as cents in `numeric`
 * columns, so the conversion shifts the decimal point on the string itself: no floating-point math.
 */

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * Cents as decimal strings with up to 4 places (the `numeric(…, 4)` columns), added, multiplied by a
 * count and compared exactly through integer ten-thousandths of a cent.
 */
const UNIT_SCALE = 4;

function toUnits(cents: string): bigint {
  const match = DECIMAL.exec(cents.trim());
  if (!match) throw new Error(`not a decimal amount: "${cents}"`);
  const [, sign = '', whole = '0', fraction = ''] = match;
  if (fraction.length > UNIT_SCALE) throw new Error(`more than ${UNIT_SCALE} decimals: "${cents}"`);
  const units = BigInt(whole + fraction.padEnd(UNIT_SCALE, '0'));
  return sign === '-' ? -units : units;
}

function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(UNIT_SCALE + 1, '0');
  const whole = digits.slice(0, -UNIT_SCALE);
  const fraction = digits.slice(-UNIT_SCALE).replace(/0+$/, '');
  const text = fraction ? `${whole}.${fraction}` : whole;
  return negative && text !== '0' ? `-${text}` : text;
}

export function addCents(a: string, b: string): string {
  return fromUnits(toUnits(a) + toUnits(b));
}

export function multiplyCents(cents: string, count: number): string {
  if (!Number.isSafeInteger(count)) throw new Error(`not a whole count: ${count}`);
  return fromUnits(toUnits(cents) * BigInt(count));
}

/** -1, 0 or 1. */
export function compareCents(a: string, b: string): number {
  const d = toUnits(a) - toUnits(b);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

/** "$0.05" style, for previews ("about $2.00"). Rounds half up to the cent. */
export function formatCentsAmount(cents: string): string {
  const units = toUnits(cents);
  const half = 10n ** BigInt(UNIT_SCALE) / 2n;
  const rounded = (units + (units >= 0n ? half : -half)) / 10n ** BigInt(UNIT_SCALE);
  const abs = rounded < 0n ? -rounded : rounded;
  const dollars = abs / 100n;
  const rest = String(abs % 100n).padStart(2, '0');
  return `${rounded < 0n ? '-' : ''}$${dollars.toLocaleString('en-US')}.${rest}`;
}

/** "0.0040" → "0.4", "1.25" → "125", "-0.5" → "-50". Null when the input is not a plain decimal. */
export function dollarsToCents(amount: string): string | null {
  const match = DECIMAL.exec(amount.trim());
  if (!match) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const padded = fraction.padEnd(2, '0');
  const integerPart = (whole + padded.slice(0, 2)).replace(/^0+(?=\d)/, '');
  const fractionPart = padded.slice(2).replace(/0+$/, '');
  const cents = fractionPart ? `${integerPart}.${fractionPart}` : integerPart;
  return cents === '0' ? '0' : `${sign}${cents}`;
}
